import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [message, setMessage] = useState('Verificando el estado de tu pago...');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    // Check for Stripe session_id
    const sessionId = searchParams.get('session_id');
    
    // Check for PayPal token
    const paypalToken = searchParams.get('token');
    const payerId = searchParams.get('PayerID');
    
    if (paypalToken && payerId) {
      // PayPal payment
      handlePayPalCapture(paypalToken);
    } else if (sessionId) {
      // Stripe payment
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
      setMessage('ID de sesión no encontrado');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attemptCount = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attemptCount >= maxAttempts) {
      setStatus('timeout');
      setMessage('La verificación del pago tomó más tiempo de lo esperado. Por favor verifica tu email o contacta soporte.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/stripe/checkout-status/${sessionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setMessage('¡Pago exitoso! Redirigiendo al dashboard...');
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        setMessage('La sesión de pago ha expirado. Por favor intenta de nuevo.');
        return;
      }

      // If still pending, continue polling
      setAttempts(attemptCount + 1);
      setTimeout(() => pollPaymentStatus(sessionId, attemptCount + 1), pollInterval);
    } catch (error) {
      setStatus('error');
      setMessage('Error al verificar el estado del pago. Por favor contacta soporte.');
    }
  };

  const handlePayPalCapture = async (orderId) => {
    try {
      setStatus('checking');
      setMessage('Procesando pago con PayPal...');

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/paypal/capture-order/${orderId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success && response.data.status === 'completed') {
        setStatus('success');
        setMessage('¡Pago con PayPal exitoso! Redirigiendo al dashboard...');
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } else if (response.data.status === 'already_captured') {
        setStatus('success');
        setMessage('El pago ya fue procesado anteriormente. Redirigiendo...');
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setStatus('error');
        setMessage('No se pudo completar el pago. Por favor contacta soporte.');
      }
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Error al procesar pago con PayPal');
    }
  };

  return (
    <div className="font-display bg-background-light dark:bg-background-dark min-h-screen">
      <div className="px-4 sm:px-10 md:px-20 lg:px-40">
        <Header />
        
        <div className="flex items-center justify-center py-20">
          <div className="max-w-md w-full bg-white dark:bg-[#1a2530] rounded-xl p-8 shadow-lg text-center">
            {status === 'checking' && (
              <>
                <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-primary mb-4"></div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Verificando Pago
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-2">{message}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Intento {attempts + 1} de 5
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
                  <span className="material-symbols-outlined text-4xl text-green-600 dark:text-green-400">
                    check_circle
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  ¡Pago Exitoso!
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-6">{message}</p>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                  <span className="material-symbols-outlined text-4xl text-red-600 dark:text-red-400">
                    error
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Error
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-6">{message}</p>
                <button
                  onClick={() => navigate('/pricing')}
                  className="px-6 py-3 bg-primary text-white rounded-lg font-bold hover:bg-primary/90"
                >
                  Volver a Planes
                </button>
              </>
            )}

            {status === 'expired' && (
              <>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 dark:bg-yellow-900/30 mb-4">
                  <span className="material-symbols-outlined text-4xl text-yellow-600 dark:text-yellow-400">
                    schedule
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Sesión Expirada
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-6">{message}</p>
                <button
                  onClick={() => navigate('/pricing')}
                  className="px-6 py-3 bg-primary text-white rounded-lg font-bold hover:bg-primary/90"
                >
                  Intentar de Nuevo
                </button>
              </>
            )}

            {status === 'timeout' && (
              <>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-900/30 mb-4">
                  <span className="material-symbols-outlined text-4xl text-gray-600 dark:text-gray-400">
                    hourglass_empty
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Verificación en Proceso
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-6">{message}</p>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="w-full px-6 py-3 bg-primary text-white rounded-lg font-bold hover:bg-primary/90"
                  >
                    Ir al Dashboard
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="w-full px-6 py-3 bg-gray-200 dark:bg-[#283039] text-gray-900 dark:text-white rounded-lg font-bold hover:bg-gray-300 dark:hover:bg-[#3b4754]"
                  >
                    Reintentar Verificación
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;